import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Checkbox, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { Button, CollapseCard } from '@totalsoft/rocket-ui';
import PropTypes from 'prop-types';
import { emptyString } from 'constants/defaultValues';
import CrossCheckFlowResultContainer from './CrossCheckFlowResultContainer';
import { useCrossCheckUtils } from './utils';
import { FlowChecksTable } from './FlowCheckStyles';

const CrossCheckFlowComponent = ({ crossCheckResults, checkData, onSelect, onSelectAll, onCheckCrossFlow, title, insuranceTypeCode }) => {
  const { t } = useTranslation();
  const { watchCrossCheckStatus } = useCrossCheckUtils();

  const allSelected = useMemo(() => checkData.every(x => x.selected), [checkData]);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    const statusSubscription = watchCrossCheckStatus(status => setDisabled(status.some(x => x.loading)));
    return () => statusSubscription.unsubscribe();
  }, [watchCrossCheckStatus]);

  const handleSelect = useCallback(id => () => onSelect(id), [onSelect]);

  return (
    <CollapseCard title={title} icon={PlaylistAddCheckIcon} defaultExpanded={false} toggleOnHeaderClick>
      <>
        {!checkData || checkData.length <= 0 ? (
          <strong>{t('Settings.InsuranceIntegrationChecks.NoDocumentsConfigurated')}</strong>
        ) : (
          <Grid container>
            <Grid item container xs={12}>
              <FormControlLabel
                disabled={false}
                control={<Checkbox id={'FlowChecks_Detail_All_Selected'} checked={allSelected} disabled={false} onChange={onSelectAll} />}
                label={t('Settings.InsuranceIntegrationChecks.SelectAllOffers')}
              />
            </Grid>
            {checkData &&
              checkData.length > 0 &&
              checkData.map((detail, index) => {
                return (
                  <Grid item container xs={12} sm={6} md={4} lg={3} key={index}>
                    <FormControlLabel
                      disabled={false}
                      control={
                        <Checkbox
                          id={'FlowChecks_Detail_Selected_' + index}
                          checked={detail.selected}
                          disabled={false}
                          onChange={handleSelect(detail.documentId)}
                        />
                      }
                      label={t('Offer.List.Field.DocumentNumberHeader', {
                        documentNumber: detail.documentNumber || emptyString,
                        documentDate: t('DATE', {
                          date: detail.documentDate || emptyString
                        })
                      })}
                    />
                  </Grid>
                );
              })}
            <Grid item container xs={12} justifyContent={'flex-start'}>
              <Button
                id='CrossCheck_Lauch'
                onClick={onCheckCrossFlow}
                disabled={disabled || (checkData && checkData.length > 0 && !checkData.some(x => x.selected))}
                sx={{ mt: 2 }}
              >
                {t('Settings.ConnectionChecks.LaunchCheck')}
              </Button>
            </Grid>
            {crossCheckResults && crossCheckResults.length > 0 && (
              <FlowChecksTable>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Settings.InsuranceIntegrationChecks.Offers')}</TableCell>
                    <TableCell>{t('Settings.ConnectionChecks.Status')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crossCheckResults.map(crossCheckResult => {
                    return (
                      <CrossCheckFlowResultContainer
                        key={crossCheckResult.documentId}
                        data={crossCheckResult}
                        insuranceTypeCode={insuranceTypeCode}
                      />
                    );
                  })}
                </TableBody>
              </FlowChecksTable>
            )}
          </Grid>
        )}
      </>
    </CollapseCard>
  );
};

CrossCheckFlowComponent.propTypes = {
  crossCheckResults: PropTypes.array,
  checkData: PropTypes.array,
  onSelect: PropTypes.func,
  onSelectAll: PropTypes.func,
  onCheckCrossFlow: PropTypes.func,
  title: PropTypes.string,
  insuranceTypeCode: PropTypes.string
};

export default CrossCheckFlowComponent;
